import { CreditCard, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { BillingPageState } from "@/features/billing/billing.types";

type BillingManagementCardProps = {
  billingState: BillingPageState;
  onOpenPortal: () => void;
};

export function BillingManagementCard({
  billingState,
  onOpenPortal,
}: BillingManagementCardProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink/78">
          {t("billing.management.eyebrow")}
        </p>
        <h3 className="text-[2rem] font-semibold leading-tight text-ink">
          {t("billing.management.paymentTitle", { defaultValue: "Payment method" })}
        </h3>
        <p className="max-w-3xl text-sm leading-7 text-ink/62">
          {t("billing.management.portalBody")}
        </p>
      </div>

      <div className="flex flex-col gap-5 rounded-[1.5rem] border border-[#e7ddd0] bg-[#fbf7f1] px-5 py-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex size-14 shrink-0 items-center justify-center rounded-[1rem] bg-white text-ink/72 ring-1 ring-[#e4d9cb]">
            <CreditCard className="size-6" />
          </div>

          <div>
            <p className="text-lg font-semibold text-ink">
              {billingState.canManageBilling
                ? t("billing.management.paymentSummary", {
                    defaultValue: "Manage cards and invoices in Stripe",
                  })
                : t("billing.management.paymentUnavailable", {
                    defaultValue: "Available after starting a paid plan",
                  })}
            </p>
            <p className="mt-1 text-sm text-ink/58">
              {t(`billing.management.status.${billingState.managementStatus}`)}
            </p>
          </div>
        </div>

        <Button
          className="min-w-[11rem] rounded-[1rem] bg-white px-6 py-3 text-base text-ink shadow-none ring-1 ring-[#e0d5c8] hover:bg-[#fffaf4]"
          disabled={!billingState.canManageBilling}
          onClick={onOpenPortal}
          type="button"
          variant="outline"
        >
          {t("billing.management.updatePayment", { defaultValue: "Update" })}
        </Button>
      </div>

      <Button
        className="justify-start px-0 text-base font-medium text-ink/72 shadow-none hover:bg-transparent hover:text-ink"
        disabled={!billingState.canManageBilling}
        onClick={onOpenPortal}
        type="button"
        variant="ghost"
      >
        <ExternalLink className="size-5" />
        {t("billing.management.invoiceHistory", { defaultValue: "View invoice history" })}
      </Button>
    </div>
  );
}
